import { Inject, Injectable, Optional, Type } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PROJECT_PROVIDER, FILTER_PROVIDER, FILTER_COMPONENT } from '../../core/tokens';
import { ProjectProvider, FilterProvider } from '../../core/interfaces';
import { StorageService } from '../../services/storage/storage.service';
import { OfflineModeService } from '../../services/offline-mode.service';
import { ProjectPointSelection } from '../project/project-points.service';

/**
 * Service für Filter-Koordination: Filter-Modal, Filter-Wiederherstellung, Projekt-Filter.
 * Extrahiert aus MapComponent (Plan 15 Phase 4).
 */
@Injectable({ providedIn: 'root' })
export class FilterCoordinationService {

  constructor(
    @Inject(PROJECT_PROVIDER) private ps: ProjectProvider,
    @Inject(FILTER_PROVIDER) private fs: FilterProvider,
    @Optional() @Inject(FILTER_COMPONENT) private filterComponentType: Type<any> | null,
    private oms: OfflineModeService,
    private modalService: NgbModal,
    private storage: StorageService,
  ) {}

  async onFilter() {
    const hasProject = !!this.ps.project();
    const offlineMode = this.oms.offlineMode();

    // Prüfen ob aktive Projekt-Auswahl vorhanden ist
    let hasProjectSelection = false;
    if (hasProject) {
      const selection = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | undefined;
      hasProjectSelection = (selection?.boundsSelections?.some(s => s.active) ?? false) ||
        (selection?.fromRegions?.length ?? 0) > 0;
    }

    if (!this.filterComponentType) return;
    const modalRef = this.modalService.open(this.filterComponentType);
    const instance = modalRef.componentInstance;

    instance.local = offlineMode;
    // Scope-Switch nicht mehr anzeigen (FilterControl entfernt)
    instance.showScopeSwitch = false;
    instance.scope = 'all';

    // Bei Filter-Anwendung: Bounds je nach Kontext setzen
    const sub = instance.filterApplied.subscribe(async (filterName: string) => {
      if (hasProject && hasProjectSelection) {
        // Mit Projekt und Auswahl: globalFilterName aktualisieren, client-seitig filtern
        await this.updateProjectGlobalFilter(filterName);
        this.fs.filtersUpdated.emit();
      } else if (hasProject) {
        // Mit Projekt aber ohne Auswahl: globalFilterName aktualisieren, wie ohne Projekt filtern
        await this.updateProjectGlobalFilter(filterName);
        this.applyFilterWithCurrentScope();
      } else if (offlineMode) {
        // Offline ohne Projekt: Alle heruntergeladenen Punkte
        this.fs.setFilterBounds(null);
        this.fs.applyFilter(filterName);
      } else {
        // Online ohne Projekt: Mit aktuellem Scope
        this.applyFilterWithCurrentScope();
      }
    });

    // Subscription cleanup wenn Modal geschlossen wird
    modalRef.closed.subscribe(() => sub.unsubscribe());
    modalRef.dismissed.subscribe(() => sub.unsubscribe());
  }

  /**
   * Aktualisiert den globalen Filter im Projekt
   */
  async updateProjectGlobalFilter(filterName: string) {
    const selection = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | undefined;
    if (selection) {
      selection.globalFilterName = filterName;
      await this.ps.setProjectValue('projectPoints', selection);
    }
  }

  /**
   * Filter zurücksetzen
   */
  async onFilterReset() {
    await this.fs.resetFilter();

    // Bei Projekt: globalFilterName löschen, aber useGlobalFilter beibehalten
    if (this.ps.project()) {
      const selection = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | undefined;
      if (selection) {
        selection.globalFilterName = undefined;
        await this.ps.setProjectValue('projectPoints', selection);
      }
    }
  }

  /**
   * Gespeicherten Filter beim Laden wiederherstellen
   */
  async restoreCurrentFilter() {
    const hasProject = !!this.ps.project();
    const offlineMode = this.oms.offlineMode();

    if (hasProject) {
      // Mit Projekt: Filter aus Projekt-Einstellungen
      const selection = await this.ps.getProjectValue('projectPoints') as ProjectPointSelection | undefined;
      if (!selection?.globalFilterName || !selection?.useGlobalFilter) return;

      const hasProjectSelection = (selection?.boundsSelections?.some(s => s.active) ?? false) ||
        (selection?.fromRegions?.length ?? 0) > 0;

      if (hasProjectSelection) {
        // Mit Auswahl: Client-seitig filtern (wird von ProjectPointsComponent gehandhabt)
      } else {
        // Ohne Auswahl: Wie ohne Projekt filtern (mit Scope)
        this.storage.setSync('currentFilter', selection.globalFilterName);
        this.applyFilterWithCurrentScope();
      }
    } else {
      // Ohne Projekt: Globalen currentFilter verwenden
      const currentFilter = this.storage.getSync('currentFilter');
      if (!currentFilter) return;

      if (offlineMode) {
        this.fs.setFilterBounds(null);
        this.fs.applyFilter(currentFilter);
      } else {
        this.applyFilterWithCurrentScope();
      }
    }
  }

  /**
   * Filter mit aktuellem Kartenausschnitt neu anwenden
   */
  onFilterRefresh() {
    this.applyFilterWithCurrentScope();
  }

  /**
   * Scope wurde geändert - Filter neu anwenden
   */
  onFilterScopeChanged() {
    this.applyFilterWithCurrentScope();
  }

  /**
   * Wendet den aktuellen Filter an (immer global ohne Bounds-Einschränkung)
   */
  applyFilterWithCurrentScope() {
    const currentFilter = this.storage.getSync('currentFilter');
    if (!currentFilter) return;

    // Globaler Filter ohne Bounds-Einschränkung
    this.fs.setFilterBounds(null);
    this.fs.applyFilter(currentFilter);
  }
}
